import { Product } from '@portal/portal-core';
import { NavigationActions } from 'actions';
import { TOOL_BUTTON_NAMES } from 'constants/user-account';
import * as ContactEmails from 'constants/contact-emails';
import {
  ACTIVE_ADMIN,
  ACTIVE_INDIVIDUAL_TRIAL,
  ACTIVE_INSTITUTIONAL_TRIAL,
  ACTIVE_LICENSE,
  EXPIRED_TRIAL,
  EXPIRING_SOON,
  INACTIVE_LICENSE,
  NO_ACTION,
} from 'constants/product-tab';
import { PRODUCT_INFO } from 'constants/products';
import Lockr from 'utils/local-store';
import React, { Fragment, ReactElement, ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'store/hooks';
import { selectLicenseStatus } from 'selectors';

import { fjButtonTypes } from 'components/primitives/fj-button';
import { BuyButton, DownloadButton, HollowButton, RenewButton } from 'components/product-buttons';
import * as utils from 'utils';
import ProductSplash from '../product-splash';
import LicenseInfoLink from './license-info-link';
import { getlatestInstallerFromJson } from './installer-json-utils';

import heroImage from '../images/seqgeq-hero.png';

const { NAMES_UI_TABS, NAMES } = Product;

// Lockr.get returns `unknown` (localStorage boundary); these keys hold installer URL strings.
const seqGeqUrlOsx = Lockr.get('seqGeqDownloadUrlOsx') as string | undefined;
const seqGeqUrlWin = Lockr.get('seqGeqDownloadUrlWin') as string | undefined;

const NEW_WINDOW_ATTRS = {
  rel: 'noopener noreferrer',
  target: '_blank',
};

interface SeqGeqTabProps {
  marketingContentOnly?: boolean;
}

interface PageContent {
  header: string;
  description: ReactNode;
  buttons: (ReactElement | null)[];
  additionalContent: ReactNode;
}

const SeqGeqTab = ({ marketingContentOnly = false }: SeqGeqTabProps): ReactElement | null => {
  const { isInstitutional, isAdmin, licenseState } = useSelector(selectLicenseStatus);

  const dispatch = useAppDispatch();
  const openLicenseDrawer = (): void => {
    dispatch(NavigationActions.openLeftDrawerWithTab(TOOL_BUTTON_NAMES.LICENSE));
  };

  const productLinks = {
    customerSupportLink: (
      <a href="mailto:techsupport@flowjo.com" title="FlowJo Customer Support">
        Customer Support
      </a>
    ),
    fjUniversityLink: (
      <a
        {...NEW_WINDOW_ATTRS}
        href="https://www.flowjo.com/learn/flowjo-university"
        title="FlowJo University"
      >
        FlowJo University
      </a>
    ),
    newsletterLink: (
      <a {...NEW_WINDOW_ATTRS} href="https://www.flowjo.com/newsletter" title="FlowJo Newsletter">
        Sign Up
      </a>
    ),
    resourcesLink: (
      <a {...NEW_WINDOW_ATTRS} href="http://info.flowjo.com/resources" title="FlowJo Resources">
        click here
      </a>
    ),
  };

  // TODO: refactor to leverage /constants/
  const productButtons = (() => {
    const chinaFlag = utils.chinaFlag();
    const freeTrialText = 'Free Trial';
    const downloadTextOsx = chinaFlag ? '下载软件 Mac' : 'Download for Mac';
    const downloadTextWin = chinaFlag ? '下载软件 Windows' : 'Download for Windows';
    const demoText = chinaFlag ? '预约演示' : 'Request Demo';
    //
    const worldwideDownloadOSX =
      seqGeqUrlOsx || getlatestInstallerFromJson('seqgeq', 'macos', 'https://cloud.flowjo.com');
    const worldwideDownloadWindows =
      seqGeqUrlWin || getlatestInstallerFromJson('seqgeq', 'windows', 'https://cloud.flowjo.com');
    const seqgeqDownloadUrlOsx = chinaFlag ? seqGeqUrlOsx : worldwideDownloadOSX;
    const seqgeqDownloadUrlWin = chinaFlag ? seqGeqUrlWin : worldwideDownloadWindows;
    const buyNowUrl = 'https://www.flowjo.com/solutions/seqgeq/request-quote';
    const freeTrialPath = `/product-trial?productName=${NAMES.seqGeq}`;

    return {
      buyButton: (
        <BuyButton
          product={NAMES_UI_TABS.seqGeq}
          buttonType={fjButtonTypes.XL}
          buyNowUrl={buyNowUrl}
          key="buy"
        />
      ),
      renewButton: (
        <RenewButton product={NAMES_UI_TABS.seqGeq} buttonType={fjButtonTypes.XL} key="renew" />
      ),
      trialButton: (
        <HollowButton
          {...NEW_WINDOW_ATTRS}
          key="trial"
          href={freeTrialPath}
          text={freeTrialText}
          target="undefined"
        />
      ),
      demoButton: (
        <HollowButton
          {...NEW_WINDOW_ATTRS}
          key="demo"
          href={`mailto:${ContactEmails.getContactEmail(
            NAMES_UI_TABS.seqGeq,
          )}?subject=SeqGeq Demo Request`}
          text={demoText}
        />
      ),
      downloadButtonOsx: (
        <DownloadButton
          {...NEW_WINDOW_ATTRS}
          key="downloadOsx"
          href={seqgeqDownloadUrlOsx}
          text={downloadTextOsx}
        />
      ),
      downloadButtonWin: (
        <DownloadButton
          {...NEW_WINDOW_ATTRS}
          key="downloadWin"
          href={seqgeqDownloadUrlWin}
          text={downloadTextWin}
        />
      ),
      adminButton: (
        <HollowButton
          key="admin"
          href={`/admin?productName=${NAMES_UI_TABS.seqGeq}`}
          text="Go to Admin"
        />
      ),
      olderInstallerVersions: (
        <a
          {...NEW_WINDOW_ATTRS}
          key="older-installer-link"
          href="https://www.flowjo.com/solutions/seqgeq/downloads"
          style={{ paddingLeft: '10px' }}
        >
          Older Versions and Release Notes
        </a>
      ),
    };
  })();

  const additionalContent = (() => {
    const chinaFlag = utils.chinaFlag();
    const blurb1Header = chinaFlag ? '全方位覆盖' : 'Data Reduction';
    const blurb1Text = chinaFlag
      ? '全面覆盖单细胞数据的每一个环节，从质控，降维，聚类到差异表达分析，可视化结果尽在掌握'
      : 'Discover, cluster, and subset populations and relationships.';
    const blurb2Header = chinaFlag ? '有宽度更有深度' : 'Identify and Explore Subpopulations';
    const blurb2Text = chinaFlag
      ? '不断丰富的插件系统，即插即用的复杂模型与算法，个性化深度分析从未如此简单。'
      : 'With tools like tSNE and PCA, identify differentially expressed genes and generate publication-ready figures with a few mouse clicks.';
    const blurb3Header = chinaFlag ? '简易上手' : 'Statistic Heat Mapping';
    const blurb3Text = chinaFlag
      ? '操作界面与同门FlowJo 相似，只需简单的鼠标点击，即可完成您的分析。且授权期间可无限次使用。'
      : 'Statistic heatmapping on individual cells, summary heatmaps, and drag-and-drop report editors.';

    return {
      blurbs: (
        <Fragment>
          <div>
            <b>{blurb1Header}</b>
            <p>{blurb1Text}</p>
          </div>
          <div>
            <b>{blurb2Header}</b>
            <p>{blurb2Text}</p>
          </div>
          <div>
            <b>{blurb3Header}</b>
            <p>{blurb3Text}</p>
          </div>
        </Fragment>
      ),
      highlights: (
        <Fragment>
          <div>
            <b>Introducing a Clustering Platform</b>
            <p>
              Now available in the Discovery band of the Workspace, allowing you to run KMeans
              clustering and KNearest-Neighbors (KNN) classification.
            </p>
          </div>
          <div>
            <b>Genes Illustrated in the Pivot Window</b>
            <p>
              (i.e. Gene View) can now be selected and filtered, allowing you to focus only on
              parameters of interest.
            </p>
            <p>
              For more details on features in this release, and a comprehensive list of known issues
              visit the exhaustive release notes.
            </p>
          </div>
        </Fragment>
      ),
    };
  })();

  const getPageContent = (): PageContent | null => {
    const { customerSupportLink, fjUniversityLink, resourcesLink } = productLinks;
    const {
      trialButton,
      demoButton,
      downloadButtonOsx,
      downloadButtonWin,
      adminButton,
      olderInstallerVersions,
    } = productButtons;
    const buyButton = isInstitutional && !isAdmin ? null : productButtons.buyButton;
    const renewButton = isInstitutional && !isAdmin ? null : productButtons.renewButton;

    const { blurbs, highlights } = additionalContent;

    const chinaFlag = utils.chinaFlag();
    const headerText = chinaFlag ? '我为科学家而生' : 'Made for Scientists. By Scientists.';
    const headerBody = chinaFlag
      ? 'BD SeqGeq 是一款具有可视化用户界面的单细胞数据分析软件，无需编写代码即可在可视化界面中导入并分析单细胞数据。让工具不再成为探索和发现的限制，帮助您回归科研问题的本身'
      : 'Import and analyze any gene expression data or list of genes and share your entire analysis with a single zip file. Explore, visualize and shape your next-gen sequencing data with interactive graphs designed to help you focus on the insights that matter most.';

    if (chinaFlag) {
      return {
        header: headerText,
        description: <Fragment>{headerBody}</Fragment>,
        buttons: [demoButton, downloadButtonOsx, downloadButtonWin],
        additionalContent: blurbs,
      };
    }
    switch (licenseState) {
      case NO_ACTION:
        return {
          header: headerText,
          description: <Fragment>{headerBody}</Fragment>,
          buttons: [
            buyButton,
            trialButton,
            demoButton,
            downloadButtonOsx,
            downloadButtonWin,
            olderInstallerVersions,
          ],
          additionalContent: blurbs,
        };

      case ACTIVE_INDIVIDUAL_TRIAL:
        return {
          header: 'Thank You for Signing Up for a SeqGeq Trial!',
          description: (
            <Fragment>
              We want to help you extract insight from your gene expression data. You can find free
              SeqGeq tutorials and training webinars on {fjUniversityLink}.
            </Fragment>
          ),
          buttons: [
            buyButton,
            demoButton,
            downloadButtonOsx,
            downloadButtonWin,
            olderInstallerVersions,
          ],
          additionalContent: highlights,
        };
      case ACTIVE_INSTITUTIONAL_TRIAL:
        return {
          header: 'Your seat is active on a SeqGeq™ trial license!',
          description: (
            <Fragment>
              We want to help you extract insight from your gene expression data. You can find free
              SeqGeq tutorials and training webinars on {fjUniversityLink}.
            </Fragment>
          ),
          buttons: [
            buyButton,
            demoButton,
            downloadButtonOsx,
            downloadButtonWin,
            olderInstallerVersions,
          ],
          additionalContent: highlights,
        };
      case EXPIRED_TRIAL:
        return {
          header: 'Continue Your Discovery, Upgrade SeqGeq™ Today!',
          description: (
            <Fragment>
              Your SeqGeq trial has expired! Ready to upgrade to a full license? Contact{' '}
              {customerSupportLink} today to discuss licensing options.
            </Fragment>
          ),
          buttons: [
            buyButton,
            demoButton,
            downloadButtonOsx,
            downloadButtonWin,
            olderInstallerVersions,
          ],
          additionalContent: blurbs,
        };

      case ACTIVE_LICENSE:
        return {
          header: 'Enhancing Your scRNA Seq Analysis',
          description: (
            <Fragment>
              You are ready to begin in-depth analysis of individual gene expression! tSNE, FlowSom,
              and PhenoGraph are faster than ever in SeqGeq.
            </Fragment>
          ),
          buttons: [downloadButtonOsx, downloadButtonWin, olderInstallerVersions],
          additionalContent: highlights,
        };

      case ACTIVE_ADMIN:
        return {
          header: 'Enhancing Your scRNA Seq Analysis',
          description: (
            <Fragment>
              You are ready to begin in-depth analysis of individual gene expression! tSNE, FlowSom,
              and PhenoGraph are faster than ever in SeqGeq.
            </Fragment>
          ),
          buttons: [adminButton, downloadButtonOsx, downloadButtonWin, olderInstallerVersions],
          additionalContent: highlights,
        };

      case INACTIVE_LICENSE:
        return {
          header: 'Continue Your Discovery, Upgrade SeqGeq Today!',
          description: (
            <Fragment>
              Your SeqGeq license has expired! Ready to renew? Contact {customerSupportLink} today
              to discuss licensing options. If you would like assistance adding single-cell
              sequencing to your grant, {resourcesLink}!
            </Fragment>
          ),
          buttons: [
            buyButton,
            demoButton,
            downloadButtonOsx,
            downloadButtonWin,
            olderInstallerVersions,
          ],
          additionalContent: blurbs,
        };
      case EXPIRING_SOON:
        return {
          header: 'SeqGeq License Will Expire Soon',
          description: (
            <Fragment>
              Your SeqGeq license will expire soon! Ready to renew? Contact {customerSupportLink}{' '}
              today to discuss licensing and payment options.
            </Fragment>
          ),
          buttons: [renewButton, downloadButtonOsx, downloadButtonWin, olderInstallerVersions],
          additionalContent: blurbs,
        };
      default:
        return null;
    }
  };

  const pageContent = getPageContent();
  if (!pageContent) {
    return null;
  }

  const licenseInfoLink = !marketingContentOnly ? (
    <LicenseInfoLink onClick={openLicenseDrawer} />
  ) : null;

  return (
    <ProductSplash
      header={pageContent.header}
      description={pageContent.description}
      additionalContent={pageContent.additionalContent}
      heroImg={{ src: heroImage, alt: 'seqgeq product preview' }}
      buttons={marketingContentOnly ? null : pageContent.buttons}
      productInfo={{
        name: PRODUCT_INFO[NAMES_UI_TABS.seqGeq].name,
        // logoSvg is a non-null svg import for SeqGeq; coerce the nullable entry type
        logoSvg: PRODUCT_INFO[NAMES_UI_TABS.seqGeq].logoSvg ?? '',
      }}
      licenseInfoLink={licenseInfoLink}
    />
  );
};

export default SeqGeqTab;
